// AGC: Card - Primary card component for Classic Navy Theme
// AGC: White cards with soft shadows on off-white background
import React from 'react'

type CardVariant = 'default' | 'elevated' | 'outlined' | 'ghost' | 'solid' | 'featured'
type CardPadding = 'none' | 'sm' | 'md' | 'lg'

interface CardProps {
  children: React.ReactNode
  className?: string
  variant?: CardVariant
  padding?: CardPadding
  onClick?: () => void
  hover?: boolean
  /** AGC: Optional accent border on left side */
  accent?: boolean
  /** AGC: Animate on mount */
  animate?: boolean
  style?: React.CSSProperties
}

/**
 * AGC: Card - Classic Navy theme card component
 * AGC: Variants:
 *   - default: White with soft shadow and border (standard cards)
 *   - elevated: White with stronger shadow (hero/important cards)
 *   - outlined: White with emphasized border (secondary content)
 *   - ghost: Transparent with subtle background (list items)
 *   - solid: White with solid shadow (forms, modals)
 *   - featured: Tinted green with accent border (special hero cards)
 */
export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  variant = 'default',
  padding = 'md',
  onClick,
  hover = false,
  accent = false,
  animate = false,
  style,
}) => {
  // AGC: Padding classes
  const paddingClasses: Record<CardPadding, string> = {
    none: '',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
  }

  // AGC: Variant classes for Classic Navy theme
  const variantClasses: Record<CardVariant, string> = {
    default: `
      bg-white dark:bg-slate-800
      border border-gray-200 dark:border-slate-700
      shadow-card
      rounded-xl
    `,
    elevated: `
      bg-white dark:bg-slate-800
      shadow-elevated
      rounded-xl
    `,
    outlined: `
      bg-white dark:bg-slate-800
      border-2 border-gray-200 dark:border-slate-600
      rounded-xl
    `,
    ghost: `
      bg-gray-50 dark:bg-slate-800/50
      rounded-lg
    `,
    solid: `
      bg-white dark:bg-slate-800
      shadow-lg
      rounded-xl
    `,
    featured: `
      bg-gradient-to-br from-emerald-900 to-emerald-950
      dark:from-emerald-950 dark:to-slate-900
      shadow-elevated
      rounded-xl
      ring-1 ring-emerald-800/50
    `,
  }

  // AGC: Hover classes for interactive cards
  const hoverClasses = hover || onClick ? `
    cursor-pointer
    transition-all duration-200 ease-out
    hover:shadow-card-hover
    hover:border-gray-300 dark:hover:border-slate-600
    active:scale-[0.98] active:ring-2 active:ring-[var(--ring-click-primary)]
  ` : ''

  // AGC: Accent border class (navy accent)
  const accentClasses = accent 
    ? 'border-l-4 border-l-navy-600' 
    : ''

  // AGC: Animation class
  const animateClasses = animate ? 'animate-fade-in-up' : ''

  // AGC: Clean whitespace from template literals
  const cleanClass = (cls: string) => cls.replace(/\s+/g, ' ').trim()

  return (
    <div
      className={cleanClass(`
        ${variantClasses[variant]}
        ${paddingClasses[padding]}
        ${hoverClasses}
        ${accentClasses}
        ${animateClasses}
        ${className}
      `)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
      style={style}
    >
      {children}
    </div>
  )
}

/**
 * AGC: CardHeader - Optional header section for cards
 */
interface CardHeaderProps {
  children: React.ReactNode
  className?: string
  action?: React.ReactNode
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  className = '',
  action,
}) => {
  return (
    <div className={`flex items-center justify-between mb-4 ${className}`}>
      <div className="flex-1">{children}</div>
      {action && <div className="flex-shrink-0 ml-2">{action}</div>}
    </div>
  )
}

/**
 * AGC: CardTitle - Styled title for card headers
 */
interface CardTitleProps {
  children: React.ReactNode
  className?: string
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export const CardTitle: React.FC<CardTitleProps> = ({
  children,
  className = '',
  as: Component = 'h3',
}) => {
  return (
    <Component className={`text-lg font-semibold text-gray-900 dark:text-white ${className}`}>
      {children}
    </Component>
  )
}

/**
 * AGC: CardDescription - Subtitle/description text
 */
interface CardDescriptionProps {
  children: React.ReactNode
  className?: string
}

export const CardDescription: React.FC<CardDescriptionProps> = ({
  children,
  className = '',
}) => {
  return (
    <p className={`text-sm text-gray-600 dark:text-gray-400 ${className}`}>
      {children}
    </p>
  )
}

/**
 * AGC: CardContent - Main content area with optional top border
 */
interface CardContentProps {
  children: React.ReactNode
  className?: string
  divider?: boolean
}

export const CardContent: React.FC<CardContentProps> = ({
  children,
  className = '',
  divider = false,
}) => {
  return (
    <div className={`${divider ? 'pt-4 border-t border-gray-200 dark:border-slate-700' : ''} ${className}`}>
      {children}
    </div>
  )
}

/**
 * AGC: CardFooter - Footer section with top border
 */
interface CardFooterProps {
  children: React.ReactNode
  className?: string
}

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className = '',
}) => {
  return (
    <div className={`mt-4 pt-4 border-t border-gray-200 dark:border-slate-700 ${className}`}>
      {children}
    </div>
  )
}

export default Card
